import {
  Component,
  Input,
  Optional,
  Self,
  OnInit,
  ChangeDetectorRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { getCountryCallingCode } from 'libphonenumber-js';
import { CountryPhone } from './phone-input.models';
import { CountriesPhoneService } from './countries-phone.service';
import { DEFAULT_COUNTRY_ISO2, resolveDefaultCountry } from './default-country.utils';
import { PhoneFormatService } from './phone-format.service';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'app-phone-input' },
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder = '';

  /** Mensaje mostrado cuando el teléfono no es válido. Permite i18n desde el padre. */
  @Input() errorMessage = 'El teléfono no es válido. Introduzca un número válido con código de país.';

  countries: CountryPhone[] = [];
  selectedCountry: CountryPhone | null = null;
  nationalNumber = '';
  disabled = false;
  private pendingCountryIso2: string | null = null;

  onChange: (value: string | null) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private countriesPhoneService: CountriesPhoneService,
    private phoneFormatService: PhoneFormatService,
    private cdr: ChangeDetectorRef,
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.countriesPhoneService.getCountries().subscribe((list) => {
      this.countries = list;
      if (this.pendingCountryIso2) {
        const found = list.find((c) => c.iso2 === this.pendingCountryIso2);
        if (found) {
          this.selectedCountry = found;
        }
        this.pendingCountryIso2 = null;
      }
      if (!this.selectedCountry && list.length) {
        this.selectedCountry = resolveDefaultCountry(list);
      }
      // Si el usuario ya escribió número antes de que cargaran los países,
      // selectedCountry era null y se emitió null; ahora re-emitir el valor correcto.
      if (this.selectedCountry && this.nationalNumber) {
        this.emitValue();
      }
      this.cdr.markForCheck();
    });
  }

  onCountryChange(country: CountryPhone): void {
    this.selectedCountry = country;
    this.emitValue();
    this.onTouched();
  }

  onNationalInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = this.phoneFormatService.normalizeRawInput(input?.value ?? '');

    if (raw.startsWith('+')) {
      const { countryCode, nationalNumber } = this.phoneFormatService.parseAsYouType(
        raw,
        this.countries,
      );
      if (countryCode && this.countries.length) {
        const country = this.countries.find((c) => c.iso2 === countryCode);
        if (country) {
          this.selectedCountry = country;
        }
      }
      this.nationalNumber = nationalNumber;
    } else {
      this.nationalNumber = raw;
    }

    if (input) {
      input.value = this.nationalNumber;
    }
    this.emitValue();
    this.cdr.markForCheck();
  }

  onBlur(): void {
    this.onTouched();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  writeValue(value: string | null): void {
    if (value === null || value === undefined) {
      this.cdr.markForCheck();
      return;
    }
    const str = String(value).trim();
    if (!str) {
      this.cdr.markForCheck();
      return;
    }
    const parsed = this.phoneFormatService.parseToNationalAndCountry(
      str,
      this.selectedCountry?.iso2 ?? DEFAULT_COUNTRY_ISO2,
    );
    if (parsed) {
      const country = this.countries.find((c) => c.iso2 === parsed.country);
      if (country) {
        this.selectedCountry = country;
      } else {
        this.pendingCountryIso2 = parsed.country;
      }
      this.nationalNumber = parsed.nationalNumber;
    } else {
      this.nationalNumber = str.replace(/\D/g, '');
    }
    this.cdr.markForCheck();
  }

  compareCountries(c1: CountryPhone | null, c2: CountryPhone | null): boolean {
    if (!c1 || !c2) {
      return c1 === c2;
    }
    return c1.iso2 === c2.iso2;
  }

  private emitValue(): void {
    const digits = (this.nationalNumber || '').replace(/\D/g, '');
    if (!digits.length) {
      this.onChange(null);
      return;
    }
    const dialCode = this.getDialCode();
    if (!dialCode) {
      this.onChange(null);
      return;
    }
    // Emitir siempre el E.164 provisional (+dialCode+digits) cuando hay dígitos,
    // para que el validador reciba un valor y pueda marcar el control como inválido
    // si el número está incompleto o no es válido. Si solo emitiéramos cuando
    // buildE164 devuelve válido, mientras el usuario escribe emitiríamos null
    // y el validador consideraría el campo "vacío" (válido).
    const provisionalE164 = '+' + dialCode + digits;
    this.onChange(provisionalE164);
  }

  /** Obtiene el dialCode actual: del país seleccionado o del país por defecto (sync). */
  private getDialCode(): string | null {
    if (this.selectedCountry) {
      return this.selectedCountry.dialCode;
    }
    try {
      return getCountryCallingCode(DEFAULT_COUNTRY_ISO2 as any);
    } catch {
      return null;
    }
  }
}